import React, { useCallback, useEffect, useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';
import AdminToggle from './AdminToggle';
import { useSiteSettings } from '../SiteSettingsContext';
import {
  fieldFromTemplate,
  JEWELRY_FIELD_TEMPLATES,
  resolveFieldTemplateKey,
  setJewelryProfiles,
  slugifyProfileId,
} from '../jewelry/jewelryProfileRegistry';
import JewelryOptionListEditor from '../jewelry/JewelryOptionListEditor';
import { sanitizeJewelryOptions } from '../jewelry/jewelryCustomization';

function prepareFieldsForSave(fields) {
  return (fields || [])
    .map((field, index) => {
      const label = String(field?.label || '').trim();
      if (!field?.type || !label) return null;
      const next = { ...field, label };
      if (!next.id) next.id = slugifyProfileId(label) || `field_${index + 1}`;
      if (next.type === 'select' && !next.optionsKey && Array.isArray(next.options)) {
        const cleaned = sanitizeJewelryOptions(next.options);
        if (cleaned.length === 0) return null;
        next.options = cleaned;
      }
      return next;
    })
    .filter(Boolean);
}

function emptyDraft() {
  return {
    id: '',
    label: '',
    hint: '',
    fields: [fieldFromTemplate(JEWELRY_FIELD_TEMPLATES[0])],
  };
}

export default function AdminJewelryProfilesPanel({ sessionToken, setStatus }) {
  const { refresh: refreshSiteSettings } = useSiteSettings();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [columnReady, setColumnReady] = useState(true);
  const [builtin, setBuiltin] = useState([]);
  const [custom, setCustom] = useState([]);
  const [hiddenBuiltinIds, setHiddenBuiltinIds] = useState([]);
  const [builtinOverrides, setBuiltinOverrides] = useState({});
  const [draft, setDraft] = useState(null);
  const [editIndex, setEditIndex] = useState(-1);
  const [editBuiltinId, setEditBuiltinId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin_jewelry_profiles.php', { headers: adminAuthHeaders(sessionToken) });
      const data = await r.json().catch(() => ({}));
      if (!data.success) {
        setStatus?.(data.error || 'Could not load jewelry profiles.');
        return;
      }
      setColumnReady(data.column_ready !== false);
      setBuiltin(Array.isArray(data.builtin) ? data.builtin : []);
      setCustom(Array.isArray(data.config?.custom) ? data.config.custom : []);
      setHiddenBuiltinIds(Array.isArray(data.config?.hiddenBuiltinIds) ? data.config.hiddenBuiltinIds : []);
      setBuiltinOverrides(
        data.config?.builtinOverrides && typeof data.config.builtinOverrides === 'object'
          ? data.config.builtinOverrides
          : {}
      );
      if (data.merged) setJewelryProfiles(data.merged);
    } catch {
      setStatus?.('Could not load jewelry profiles.');
    } finally {
      setLoading(false);
    }
  }, [sessionToken, setStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (nextCustom, nextHidden, nextOverrides = builtinOverrides) => {
    setSaving(true);
    try {
      const r = await fetch('/api/admin_jewelry_profiles.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...adminAuthHeaders(sessionToken) },
        body: JSON.stringify({
          custom: nextCustom,
          hiddenBuiltinIds: nextHidden,
          builtinOverrides: nextOverrides,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!data.success) {
        setStatus?.(data.error || 'Could not save jewelry profiles.');
        return false;
      }
      setCustom(data.config?.custom || nextCustom);
      setHiddenBuiltinIds(data.config?.hiddenBuiltinIds || nextHidden);
      setBuiltinOverrides(
        data.config?.builtinOverrides && typeof data.config.builtinOverrides === 'object'
          ? data.config.builtinOverrides
          : nextOverrides
      );
      if (data.merged) setJewelryProfiles(data.merged);
      try {
        await refreshSiteSettings();
      } catch {
        /* registry already updated from jewelry API merged payload */
      }
      if (Array.isArray(data.warnings) && data.warnings.length > 0) {
        setStatus?.(data.warnings.join(' '));
      } else {
        setStatus?.('Jewelry customization types saved.');
      }
      return true;
    } catch {
      setStatus?.('Could not save jewelry profiles.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const toggleBuiltinVisible = async (id, visible) => {
    const nextHidden = visible
      ? hiddenBuiltinIds.filter((x) => x !== id)
      : [...hiddenBuiltinIds, id];
    setHiddenBuiltinIds(nextHidden);
    await save(custom, nextHidden, builtinOverrides);
  };

  const startAdd = () => {
    setEditIndex(-1);
    setEditBuiltinId('');
    setDraft(emptyDraft());
  };

  const startEdit = (index) => {
    setEditIndex(index);
    setEditBuiltinId('');
    setDraft(JSON.parse(JSON.stringify(custom[index])));
  };

  const startEditBuiltin = (profile) => {
    setEditIndex(-1);
    setEditBuiltinId(profile.id);
    const override = builtinOverrides[profile.id];
    setDraft(JSON.parse(JSON.stringify(override || profile)));
  };

  const cancelDraft = () => {
    setDraft(null);
    setEditIndex(-1);
    setEditBuiltinId('');
  };

  const saveDraft = async () => {
    const label = String(draft?.label || '').trim();
    if (!label) {
      setStatus?.('Profile label is required.');
      return;
    }
    const id = editBuiltinId || String(draft?.id || '').trim() || slugifyProfileId(label);
    const fields = prepareFieldsForSave(draft?.fields);
    if (fields.length === 0) {
      setStatus?.('Add at least one customer field with a label. Custom dropdowns need at least one choice name.');
      return;
    }
    const entry = {
      id,
      label,
      hint: String(draft?.hint || '').trim(),
      builtin: Boolean(editBuiltinId),
      fields,
    };

    if (editBuiltinId) {
      const nextOverrides = { ...builtinOverrides, [editBuiltinId]: entry };
      const ok = await save(custom, hiddenBuiltinIds, nextOverrides);
      if (ok) cancelDraft();
      return;
    }

    const next = [...custom];
    if (editIndex >= 0) next[editIndex] = { ...entry, builtin: false };
    else next.push({ ...entry, builtin: false });
    const ok = await save(next, hiddenBuiltinIds, builtinOverrides);
    if (ok) cancelDraft();
  };

  const resetBuiltinOverride = async (id) => {
    const nextOverrides = { ...builtinOverrides };
    delete nextOverrides[id];
    await save(custom, hiddenBuiltinIds, nextOverrides);
  };

  const deleteCustom = async (index) => {
    const next = custom.filter((_, i) => i !== index);
    await save(next, hiddenBuiltinIds, builtinOverrides);
  };

  if (loading) {
    return <p className="text-muted small mb-0">Loading jewelry customization types…</p>;
  }

  return (
    <div className="admin-jewelry-profiles">
      {!columnReady ? (
        <div className="alert alert-warning small">
          Custom jewelry types cannot be saved until you run{' '}
          <code>database/migrate_jewelry_profiles_settings.sql</code> in phpMyAdmin (or run{' '}
          <code>php scripts/ensure-jewelry-profiles-column.php</code> locally).
        </div>
      ) : null}

      <p className="text-muted small">
        Define customization types for the product editor dropdown. Built-in types cover common cases; add your own for
        maang tikka, haldi sets, etc. Then assign a type per product under <strong>Products → Edit → Jewelry customization</strong>.
      </p>

      <section className="mb-4">
        <h3 className="h6">Built-in types</h3>
        <p className="small text-muted">Hide types you don&apos;t use, or edit a built-in type to change its fields and labels.</p>
        <ul className="list-unstyled mb-0">
          {builtin.map((p) => {
            const customized = Boolean(builtinOverrides[p.id]);
            const display = customized ? builtinOverrides[p.id] : p;
            return (
            <li key={p.id} className="admin-jewelry-profiles__row border rounded p-2 mb-2">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <strong>{display.label}</strong>
                  {customized ? (
                    <span className="badge text-bg-light border ms-1 align-middle">Customized</span>
                  ) : null}
                  <span className="small text-muted d-block">ID: {p.id}</span>
                  {display.hint ? <span className="small text-muted d-block">{display.hint}</span> : null}
                  <span className="small text-muted d-block">
                    Fields: {(display.fields || []).map((f) => f.label).join(', ')}
                  </span>
                </div>
                <AdminToggle
                  id={`builtin-visible-${p.id}`}
                  label="Show in product dropdown"
                  checked={!hiddenBuiltinIds.includes(p.id)}
                  onChange={(v) => toggleBuiltinVisible(p.id, v)}
                  disabled={saving || Boolean(draft)}
                />
              </div>
              <div className="mt-2 d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => startEditBuiltin(p)}
                  disabled={Boolean(draft)}
                >
                  Edit
                </button>
                {customized ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => resetBuiltinOverride(p.id)}
                    disabled={saving || Boolean(draft)}
                  >
                    Reset to default
                  </button>
                ) : null}
              </div>
            </li>
            );
          })}
        </ul>
      </section>

      <section className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h3 className="h6 mb-0">Your custom types</h3>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={startAdd} disabled={Boolean(draft)}>
            Add custom type
          </button>
        </div>
        {custom.length === 0 ? (
          <p className="small text-muted mb-0">No custom types yet.</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {custom.map((p, index) => (
              <li key={p.id} className="admin-jewelry-profiles__row border rounded p-2 mb-2">
                <strong>{p.label}</strong>
                <span className="small text-muted d-block">ID: {p.id}</span>
                <span className="small text-muted d-block">
                  Fields: {(p.fields || []).map((f) => f.label).join(', ')}
                </span>
                <div className="mt-2 d-flex gap-2">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(index)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deleteCustom(index)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {draft ? (
        <section className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6">
              {editBuiltinId
                ? 'Edit built-in type'
                : editIndex >= 0
                  ? 'Edit custom type'
                  : 'New custom type'}
            </h3>
            {editBuiltinId ? (
              <p className="small text-muted">
                Changes apply to this built-in type store-wide. ID stays <code>{editBuiltinId}</code>.
              </p>
            ) : null}
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small">Label (shown in dropdown)</label>
                <input
                  className="form-control form-control-sm"
                  value={draft.label}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      label: e.target.value,
                      id: d.id || slugifyProfileId(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">ID (slug, auto-generated)</label>
                <input
                  className="form-control form-control-sm font-monospace"
                  value={draft.id}
                  readOnly={Boolean(editBuiltinId)}
                  onChange={(e) => setDraft((d) => ({ ...d, id: slugifyProfileId(e.target.value) }))}
                />
              </div>
              <div className="col-12">
                <label className="form-label small">Hint for admin (optional)</label>
                <input
                  className="form-control form-control-sm"
                  value={draft.hint || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, hint: e.target.value }))}
                />
              </div>
            </div>

            <h4 className="h6 mt-4">Customer fields</h4>
            {(draft.fields || []).map((field, fi) => (
              <div key={fi} className="border rounded p-2 mb-2">
                <div className="row g-2 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label small">Field label</label>
                    <input
                      className="form-control form-control-sm"
                      value={field.label || ''}
                      onChange={(e) => {
                        const label = e.target.value;
                        setDraft((d) => {
                          const fields = [...d.fields];
                          fields[fi] = { ...fields[fi], label, id: fields[fi].id || slugifyProfileId(label) };
                          return { ...d, fields };
                        });
                      }}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Type</label>
                    <select
                      className="form-select form-select-sm"
                      value={resolveFieldTemplateKey(field)}
                      onChange={(e) => {
                        const tpl = JEWELRY_FIELD_TEMPLATES.find((t) => t.key === e.target.value);
                        if (!tpl) return;
                        setDraft((d) => {
                          const fields = [...d.fields];
                          fields[fi] = fieldFromTemplate(tpl, {
                            id: fields[fi]?.id,
                            label: fields[fi]?.label || tpl.defaults.label,
                          });
                          return { ...d, fields };
                        });
                      }}
                    >
                      {JEWELRY_FIELD_TEMPLATES.map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <AdminToggle
                      id={`field-opt-${fi}`}
                      label="Optional field"
                      checked={Boolean(field.optional)}
                      onChange={(v) =>
                        setDraft((d) => {
                          const fields = [...d.fields];
                          const next = { ...fields[fi] };
                          if (v) {
                            next.optional = true;
                            delete next.required;
                          } else {
                            delete next.optional;
                            next.required = true;
                          }
                          fields[fi] = next;
                          return { ...d, fields };
                        })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger w-100"
                      onClick={() =>
                        setDraft((d) => ({ ...d, fields: d.fields.filter((_, i) => i !== fi) }))
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
                {field.type === 'select' && !field.optionsKey ? (
                  <div className="mt-2">
                    <label className="form-label small mb-1">Customer choices</label>
                    <JewelryOptionListEditor
                      options={field.options}
                      showPrice
                      onChange={(next) =>
                        setDraft((d) => {
                          const fields = [...d.fields];
                          fields[fi] = { ...fields[fi], options: next };
                          return { ...d, fields };
                        })
                      }
                    />
                  </div>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  fields: [...(d.fields || []), fieldFromTemplate(JEWELRY_FIELD_TEMPLATES[0])],
                }))
              }
            >
              Add field
            </button>

            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-primary btn-sm" onClick={saveDraft} disabled={saving || !columnReady}>
                {saving ? 'Saving…' : 'Save type'}
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={cancelDraft}>
                Cancel
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
